import React, { useState, useEffect, useContext } from 'react';
import { getDisplayStoreNumber } from '../../../utils/storeNumber';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Send as SendIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import {
  TrainingStatus,
} from '../Enums/complianceEnums';
import { trainingClient, TrainingModuleDto, TrainingFranchiseOptionDto, TrainingBundleDto, TrainingBundleType, TrainingAssignmentDto } from '../../../app/services/api/compliance/training';
import { UserContext } from '../../../components/shared/useUser';
import { Plus, SearchSm } from '../../../assets';
import PageArea from '../../../components/shared/PageArea';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import CustomPagination from '../../../components/forms/Pagination';
import { JobRole, TrainingAssignment } from '../Interfaces/complicanceInterfaces';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';

interface BulkAssignmentForm {
  contentType: 'module' | 'course' | 'pack';
  moduleId: number;
  bundleId: number;
  assignmentType: 'user' | 'role' | 'store';
  storeNumber?: string;
  userIds: number[];
  roleIds: number[];
  storeNumbers: string[];
  dueDate: string;
  sendNotification: boolean;
  notificationMessage: string;
}

interface User {
  id: number;
  name: string;
  email: string;
}

// Helper function to map API status to TrainingStatus enum
// Backend uses: Assigned=0, InProgress=1, Completed=2, Failed=3
// Frontend uses: Draft=0, Published=1, Archived=2, Assigned=3, InProgress=4, Completed=5, Failed=6, Expired=7
const mapStatusToEnum = (status: string | number): TrainingStatus => {
  // If it's a number, map from backend enum values
  if (typeof status === 'number') {
    switch (status) {
      case 0: return TrainingStatus.Assigned;
      case 1: return TrainingStatus.InProgress;
      case 2: return TrainingStatus.Completed;
      case 3: return TrainingStatus.Failed;
      case 4:
      case 7:
        return TrainingStatus.Expired;
      default:
        return TrainingStatus.Assigned;
    }
  }

  // If it's a string, map from string status names
  switch (status.toLowerCase()) {
    case 'pending':
    case 'assigned':
      return TrainingStatus.Assigned;
    case 'inprogress':
    case 'in progress':
      return TrainingStatus.InProgress;
    case 'completed':
      return TrainingStatus.Completed;
    case 'failed':
      return TrainingStatus.Failed;
    case 'expired':
      return TrainingStatus.Expired;
    case 'draft':
      return TrainingStatus.Draft;
    case 'published':
      return TrainingStatus.Published;
    case 'archived':
      return TrainingStatus.Archived;
    default:
      return TrainingStatus.Assigned;
  }
};

const localeLanguage =
  typeof navigator !== 'undefined'
    ? (navigator.languages?.[0] || navigator.language)
    : undefined;

const TrainingAssignments: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { selectedStore, selectedFranchise, hasPermissionTo } = useContext(UserContext);
  const { t, i18n } = useTranslation('training');
  const canManageAssignments = hasPermissionTo(['TrainingAdmin', 'TrainingAssignmentManager']);

  const canAssignCoursesPacks = hasPermissionTo(['TrainingAdmin', 'TrainingAssignmentManager']);

  // State
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [modules, setModules] = useState<TrainingModuleDto[]>([]);
  const [bundles, setBundles] = useState<TrainingBundleDto[]>([]);
  const [roles, setRoles] = useState<JobRole[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [bulkAssignUsers, setBulkAssignUsers] = useState<User[]>([]);
  const [loadingBulkUsers, setLoadingBulkUsers] = useState(false);
  const [stores, setStores] = useState<{ storeNumber: string; storeName: string }[]>([]);
  const [totalAssignmentsCount, setTotalAssignmentsCount] = useState(0);

  const [filterFranchises, setFilterFranchises] = useState<TrainingFranchiseOptionDto[]>([]);
  const [filterFranchiseId, setFilterFranchiseId] = useState<number | null>(null);
  const effectiveFranchiseId = filterFranchiseId ?? selectedFranchise?.franchiseId ?? null;
  const [filterFranchiseStores, setFilterFranchiseStores] = useState<{ storeNumber: string; storeName: string }[]>([]);
  const [filterStoreNumber, setFilterStoreNumber] = useState<string>('');
  const [loadingFilterStores, setLoadingFilterStores] = useState(false);

  const [filterStatus, setFilterStatus] = useState<TrainingStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  // committedSearch is what was last submitted via the Search button or a clear action
  const [committedSearch, setCommittedSearch] = useState('');

  // Dialogs
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<TrainingAssignment | null>(null);

  // Bulk assignment form
  const [bulkForm, setBulkForm] = useState<BulkAssignmentForm>({
    contentType: 'module',
    moduleId: 0,
    bundleId: 0,
    assignmentType: 'role',
    storeNumber: selectedStore?.storeNumber || '',
    userIds: [],
    roleIds: [],
    storeNumbers: [],
    dueDate: '',
    sendNotification: true,
    notificationMessage: '',
  });
  const selectedContentId = bulkForm.contentType === 'module' ? bulkForm.moduleId : bulkForm.bundleId;

  // Pagination
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  useEffect(() => {
    loadSupportingData();
  }, [selectedStore?.storeNumber, selectedFranchise?.franchiseId]);

  useEffect(() => {
    loadAssignments();
  }, [selectedStore?.storeNumber, filterStatus, page, committedSearch, filterStoreNumber, effectiveFranchiseId]);

  useEffect(() => {

    trainingClient.getAccessibleFranchises(selectedFranchise?.franchiseId)
      .then(data => {
        setFilterFranchises(data);
        // Auto-select the first franchise when no franchise is pre-selected
        if (!filterFranchiseId && !selectedFranchise?.franchiseId && data.length > 0) {
          setFilterFranchiseId(data[0].id);
        }
      })
      .catch(() => setFilterFranchises([]));
  }, []);

  useEffect(() => {
    if (!effectiveFranchiseId) {
      setFilterFranchiseStores([]);
      setFilterStoreNumber('');
      return;
    }
    setLoadingFilterStores(true);
    trainingClient.getStoresByFranchise(effectiveFranchiseId)
      .then(data => setFilterFranchiseStores(data.filter(s => s.storeNumber)))
      .catch(() => setFilterFranchiseStores([]))
      .finally(() => setLoadingFilterStores(false));
  }, [effectiveFranchiseId]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const getApiStatusId = (status: TrainingStatus | 'all'): number | undefined => {
    if (status === 'all') {
      return undefined;
    }

    switch (status) {
      case TrainingStatus.Assigned:
        return 0;
      case TrainingStatus.InProgress:
        return 1;
      case TrainingStatus.Completed:
        return 2;
      case TrainingStatus.Failed:
        return 3;
      default:
        return Number(status);
    }
  };

  const mapAssignments = (assignmentsData: TrainingAssignmentDto[]): TrainingAssignment[] => {
    return assignmentsData.map(a => ({
      id: a.id,
      userId: a.userId,
      userName: a.userEmail || `User ${a.userId}`,
      userEmail: a.userEmail || '',
      moduleId: a.moduleId,
      moduleName: a.moduleName,
      bundleId: a.bundleId,
      bundleName: a.bundleName,
      bundleType: a.bundleType,
      assignedDate: a.assignedDate,
      dueDate: a.dueDate,
      completedDate: a.completedDate || null,
      status: mapStatusToEnum(a.status),
      progress: a.totalSteps > 0 ? (a.currentStep / a.totalSteps) * 100 : 0,
      score: a.score || null,
      attemptCount: 0,
    }));
  };

  const loadSupportingData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!selectedStore?.storeNumber) {
        setError(t('trainingAssignments.noStoreSelected'));
        return;
      }

      const franchiseId = selectedFranchise?.franchiseId || 0;

      // Use Promise.allSettled to allow some API calls to fail without blocking others
      const results = await Promise.allSettled([
        trainingClient.getModules({ franchiseId, status: 1, includeInactive: false }), // Only get Published modules (status=1)
        trainingClient.getUsersByStore(selectedStore.storeNumber),
        trainingClient.getPersonasByStore(selectedStore.storeNumber),
        trainingClient.getStoresByFranchise(franchiseId),
        trainingClient.getBundles({ franchiseId, status: 1 }), // Only get Published courses/packs (status=1)
      ]);

      // Extract data from settled promises, with fallbacks for failures
      const modulesData = results[0].status === 'fulfilled' ? results[0].value : [];
      const usersData = results[1].status === 'fulfilled' ? results[1].value : [];
      const personasData = results[2].status === 'fulfilled' ? results[2].value : [];
      const storesResponse = results[3].status === 'fulfilled' ? results[3].value : [];
      const bundlesData = results[4].status === 'fulfilled' ? results[4].value : [];
      setStores(storesResponse.filter(s => s.storeNumber));

      setModules(modulesData); // API already returns the correct modules
      setBundles(bundlesData);
      setRoles(personasData.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description || '',
        isActive: true,
        requiredTrainingModuleIds: [],
        requiredTrainingModuleNames: [],
      })));
      setUsers(usersData.map(u => ({ id: u.id, name: u.name, email: u.email })));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('trainingAssignments.failedToLoadData');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      setTableLoading(true);
      setError(null);

      const effectiveStoreNumber = filterStoreNumber || '';

      const response = await trainingClient.getAllAssignments({
        franchiseId: effectiveFranchiseId ?? undefined,
        storeNumber: effectiveStoreNumber,
        search: committedSearch.trim() || undefined,
        statusId: getApiStatusId(filterStatus),
        skip: page * rowsPerPage,
        take: rowsPerPage,
      });

      setAssignments(mapAssignments(response.data));
      setTotalAssignmentsCount(response.totalCount ?? 0);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('trainingAssignments.failedToLoadData');
      setAssignments([]);
      setTotalAssignmentsCount(0);
      setError(errorMessage);
    } finally {
      setTableLoading(false);
    }
  };
  const handleOpenBulkAssign = async () => {
    if (!canManageAssignments) return;

    setBulkForm({
      contentType: 'module',
      moduleId: 0,
      bundleId: 0,
      assignmentType: 'role',
      storeNumber: selectedStore?.storeNumber || '',
      userIds: [],
      roleIds: [],
      storeNumbers: [],
      dueDate: '',
      sendNotification: true,
      notificationMessage: '',
    });
    setBulkAssignUsers([]);
    setBulkAssignDialogOpen(true);

    if (effectiveFranchiseId) {
      try {
        setLoadingBulkUsers(true);
        const usersData = await trainingClient.getUsersByFranchise(effectiveFranchiseId);
        setBulkAssignUsers(usersData.map(u => ({ id: u.id, name: u.name, email: u.email })));
      } catch (err) {
        setBulkAssignUsers([]);
        setError(err instanceof Error ? err.message : t('trainingAssignments.failedToLoadData'));
      } finally {
        setLoadingBulkUsers(false);
      }
    }
  };

  const handleBulkAssign = async () => {
    if (!canManageAssignments) return;

    if (bulkForm.contentType === 'module' && bulkForm.moduleId === 0) {
      setError(t('trainingAssignments.selectModuleRequired'));
      return;
    }

    if ((bulkForm.contentType === 'course' || bulkForm.contentType === 'pack') && bulkForm.bundleId === 0) {
      setError(t('trainingAssignments.selectBundleRequired'));
      return;
    }

    if (bulkForm.assignmentType === 'user' && bulkForm.userIds.length === 0) {
      setError(t('trainingAssignments.selectUserRequired'));
      return;
    }

    if (bulkForm.assignmentType === 'role' && bulkForm.roleIds.length === 0) {
      setError(t('trainingAssignments.selectRoleRequired'));
      return;
    }

    if (bulkForm.assignmentType === 'store' && bulkForm.storeNumbers.length === 0) {
      setError(t('trainingAssignments.selectStoreRequired'));
      return;
    }

    if (!bulkForm.dueDate) {
      setError(t('trainingAssignments.selectDueDateRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Call the bulk assign API
      const result = (bulkForm.contentType === 'course' || bulkForm.contentType === 'pack')
        ? await trainingClient.bulkAssignBundle({
          bundleId: bulkForm.bundleId,
          assignmentType: bulkForm.assignmentType,
          storeNumber: bulkForm.storeNumber,
          userIds: bulkForm.assignmentType === 'user' ? bulkForm.userIds : undefined,
          roleIds: bulkForm.assignmentType === 'role' ? bulkForm.roleIds : undefined,
          storeNumbers: bulkForm.assignmentType === 'store' ? bulkForm.storeNumbers : undefined,
          dueDate: bulkForm.dueDate || null,
          sendNotification: bulkForm.sendNotification,
          notificationMessage: bulkForm.notificationMessage || null,
        })
        : await trainingClient.bulkAssign({
          moduleId: bulkForm.moduleId,
          assignmentType: bulkForm.assignmentType,
          storeNumber: bulkForm.storeNumber,
          userIds: bulkForm.assignmentType === 'user' ? bulkForm.userIds : undefined,
          roleIds: bulkForm.assignmentType === 'role' ? bulkForm.roleIds : undefined,
          storeNumbers: bulkForm.assignmentType === 'store' ? bulkForm.storeNumbers : undefined,
          dueDate: bulkForm.dueDate || null,
          sendNotification: bulkForm.sendNotification,
          notificationMessage: bulkForm.notificationMessage || null,
        });

      setSuccess(t('trainingAssignments.assignmentsCreated', { count: result.assignedCount }));
      setBulkAssignDialogOpen(false);
      loadAssignments();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('trainingAssignments.failedToCreateAssignments'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = (assignment: TrainingAssignment) => {
    setSelectedAssignment(assignment);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAssignment) return;

    try {
      setSaving(true);
      setError(null);

      // Call the delete API
      await trainingClient.deleteAssignment(selectedAssignment.id);

      setSuccess(t('trainingAssignments.deleteSuccess'));
      setDeleteDialogOpen(false);
      setSelectedAssignment(null);
      const nextTotalCount = Math.max(totalAssignmentsCount - 1, 0);
      const nextMaxPages = Math.max(Math.ceil(nextTotalCount / rowsPerPage), 1);
      if (page >= nextMaxPages) {
        setPage(Math.max(nextMaxPages - 1, 0));
      } else {
        loadAssignments();
      }
    } catch (err) {
      setDeleteDialogOpen(false);
      setSelectedAssignment(null);
      setError(err instanceof Error ? err.message : t('trainingAssignments.failedToDeleteAssignment'));
    } finally {
      setSaving(false);
    }
  };


  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedAssignment(null);
  };

  const handleResendNotification = async (assignmentId: number) => {
    try {
      setSaving(true);
      setError(null);

      const franchiseId = selectedFranchise?.franchiseId;
      await trainingClient.sendNotification(assignmentId, franchiseId);

      setSuccess(t('trainingAssignments.notificationSent'));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('trainingAssignments.failedToSendNotification');
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getStatusLabel = (status: TrainingStatus): string => {
    switch (status) {
      case TrainingStatus.Draft:
        return 'Draft';
      case TrainingStatus.Published:
        return 'Published';
      case TrainingStatus.Archived:
        return 'Archived';
      case TrainingStatus.Assigned:
        return 'Assigned';
      case TrainingStatus.InProgress:
        return 'In Progress';
      case TrainingStatus.Completed:
        return 'Completed';
      case TrainingStatus.Failed:
        return 'Failed';
      case TrainingStatus.Expired:
        return 'Expired';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: TrainingStatus): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case TrainingStatus.Assigned:
        return 'warning';
      case TrainingStatus.InProgress:
        return 'primary';
      case TrainingStatus.Completed:
        return 'success';
      case TrainingStatus.Failed:
        return 'error';
      default:
        return 'default';
    }
  };

  const maxPages = () => {
    return Math.ceil(totalAssignmentsCount / rowsPerPage);
  };

  if (loading) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          {t('common.loading')}
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      {/* Header */}
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 4 }}
      >
        <PageTitle subTitle={t('trainingAssignments.subtitle')}>
          {t('trainingAssignments.title')}
        </PageTitle>
        {canManageAssignments && (
          <Button
            variant="primary"
            size="lg"
            startIcon={<SvgIcon><Plus /></SvgIcon>}
            onClick={handleOpenBulkAssign}
            disabled={saving}
          >
            {t('trainingAssignments.bulkAssign')}
          </Button>
        )}
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 3 }}
        >
          {success}
        </Alert>
      )}

      {/* Filter */}
      <Box
        sx={{
          mt: 5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        {filterFranchises.length > 1 && (
          <TextField
            select
            label={t('trainingAssignments.filterByFranchise')}
            value={filterFranchiseId ?? selectedFranchise?.franchiseId ?? ''}
            onChange={(e) => {

              const val = e.target.value;
              setFilterFranchiseId(val !== '' ? Number(val) : null);
              setPage(0);
            }}
            size="small"
            sx={{ width: { xs: '100%', sm: 200 } }}
          >
            {filterFranchises.map((f) => (
              <MenuItem
                key={f.id}
                value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label={t('trainingAssignments.filterByStore')}
          value={filterStoreNumber}
          onChange={(e) => {

            setFilterStoreNumber(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: { xs: '100%', sm: 200 } }}
          disabled={!effectiveFranchiseId || loadingFilterStores}
        >
          <MenuItem value="">{t('trainingAssignments.allStores')}</MenuItem>
          {filterFranchiseStores.map((s) => (
            <MenuItem
              key={s.storeNumber}
              value={s.storeNumber}>
              {s.storeName} ({s.storeNumber})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={t('trainingAssignments.status')}
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value as TrainingStatus | 'all');
            setPage(0);
          }}
          size="small"
          sx={{ width: { xs: '100%', sm: 200 } }}
        >
          <MenuItem value="all">{t('trainingAssignments.allStatuses')}</MenuItem>
          <MenuItem value={TrainingStatus.Assigned}>{t('trainingAssignments.assigned')}</MenuItem>
          <MenuItem value={TrainingStatus.InProgress}>{t('trainingAssignments.inProgress')}</MenuItem>
          <MenuItem value={TrainingStatus.Completed}>{t('trainingAssignments.completed')}</MenuItem>
          <MenuItem value={TrainingStatus.Expired}>{t('trainingAssignments.expired')}</MenuItem>
          <MenuItem value={TrainingStatus.Failed}>{t('trainingAssignments.failed')}</MenuItem>
        </TextField>

      </Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >

        <TextField
          label={t('trainingAssignments.searchAssignments')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(0);
              setCommittedSearch(searchTerm);
            }
          }}
          size="small"
          sx={{ width: { xs: '100%', sm: 200 } }}
          InputProps={{
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchTerm('');
                    setCommittedSearch('');
                    setPage(0);
                  }}
                  edge="end"
                  aria-label={t('trainingAssignments.clearSearch')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Button
          variant="primary"
          size="lg"

          startIcon={<SvgIcon><SearchSm /></SvgIcon>}
          onClick={() => {
            setPage(0);
            setCommittedSearch(searchTerm);
          }}
        >
          {t('trainingAssignments.search')}
        </Button>
      </Box>
      {/* Assignments Table */}
      <Section>
        {tableLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            {t('common.loading')}
          </Box>
        ) : assignments.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text={t('trainingAssignments.emptyText')}
            searchTerm={searchTerm}
            onClear={() => {
              setSearchTerm('');
              setCommittedSearch('');
              setFilterStatus('all');
              setPage(0);
            }}
            onAdd={handleOpenBulkAssign}
            onAddIsCreating={false}
            addItemText={t('trainingAssignments.bulkAssign')}
            onAddIsDisabled={saving}
            showOnAdd={canManageAssignments}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  {t('trainingAssignments.title')}
                </Typography>
                <Pill text={`${totalAssignmentsCount} ${totalAssignmentsCount === 1 ? t('trainingAssignments.assignment') : t('trainingAssignments.assignments')}`} />
              </Box>
            </Box>

            <TableContainer sx={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <Table sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('trainingAssignments.user')}</TableCell>
                    <TableCell>{t('trainingCoursesPacks.type')}</TableCell>
                    <TableCell>{t('trainingAssignments.itemTitle')}</TableCell>
                    <TableCell>{t('trainingAssignments.assignedDate')}</TableCell>
                    <TableCell>{t('trainingAssignments.dueDate')}</TableCell>
                    <TableCell>{t('trainingAssignments.completedDate')}</TableCell>
                    <TableCell align="center">{t('trainingAssignments.status')}</TableCell>
                    <TableCell align="right">{t('trainingAssignments.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((assignment) => {
                    const isCourse = assignment.bundleType === TrainingBundleType.Course;
                    const isPackModule = assignment.bundleType === TrainingBundleType.Pack;
                    const typeLabel = isCourse
                      ? t('trainingAssignments.course')
                      : t('trainingAssignments.singleModule');
                    const detailLabel = isCourse && assignment.bundleName
                      ? assignment.bundleName
                      : assignment.moduleName;

                    return (
                      <TableRow
                        key={assignment.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: theme.palette.custom.gray[50],
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="textSM"
                            fontWeight={500}
                          >
                            {assignment.userName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="textSM"
                            fontWeight={500}
                          >
                            {typeLabel}
                            {isPackModule && assignment.bundleName && ` (${assignment.bundleName})`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={detailLabel}
                            placement="top"
                          >
                            <Typography
                              variant="textSM"
                              fontWeight={500}
                              sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {detailLabel}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="textSM">
                            {assignment.assignedDate
                              ? new Date(assignment.assignedDate).toLocaleDateString(localeLanguage)
                              : t('trainingAssignments.notAvailable')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="textSM">
                            {assignment.dueDate
                              ? new Date(assignment.dueDate).toLocaleDateString(localeLanguage)
                              : t('trainingAssignments.noDueDate')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="textSM">
                            {assignment.completedDate
                              ? new Date(assignment.completedDate).toLocaleDateString(localeLanguage)
                              : '-'}

                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getStatusLabel(assignment.status)}
                            color={getStatusColor(assignment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {canManageAssignments && assignment.status !== TrainingStatus.Completed && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResendNotification(assignment.id);
                                }}
                                disabled={saving}
                                title={t('trainingAssignments.resendNotification')}
                              >
                                <SendIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAssignment(assignment);
                                }}
                                disabled={saving}
                                color="error"
                                title={t('trainingAssignments.deleteAssignment')}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </>
        )}
      </Section>

      {/* Bulk Assign Dialog */}
      <Dialog
        open={bulkAssignDialogOpen}
        onClose={() => setBulkAssignDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('trainingAssignments.bulkAssignTraining')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Content Type Selection */}
            <FormControl fullWidth
              required>
              <InputLabel>{t('trainingAssignments.contentType')}</InputLabel>
              <Select
                value={bulkForm.contentType}
                onChange={(e) =>
                  setBulkForm({
                    ...bulkForm,
                    contentType: e.target.value as 'module' | 'course' | 'pack',
                    moduleId: 0,
                    bundleId: 0,
                    userIds: [],
                  })
                }
                label={t('trainingAssignments.contentType')}
              >
                <MenuItem value="module">{t('trainingAssignments.singleModule')}</MenuItem>
                {canAssignCoursesPacks && (
                  <MenuItem value="course">{t('trainingAssignments.course')}</MenuItem>
                )}
                {canAssignCoursesPacks && (
                  <MenuItem value="pack">{t('trainingAssignments.pack')}</MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Module Selection */}
            {bulkForm.contentType === 'module' ? (
              <FormControl fullWidth
                required>
                <InputLabel>{t('trainingAssignments.trainingModule')}</InputLabel>
                <Select
                  value={bulkForm.moduleId}
                  onChange={(e) => {
                    const selectedId = Number(e.target.value);
                    setBulkForm({ ...bulkForm, moduleId: selectedId, userIds: [] });
                  }}
                  label={t('trainingAssignments.trainingModule')}
                >
                  <MenuItem value={0}>{t('trainingAssignments.selectModule')}</MenuItem>
                  {modules.map((module) => (
                    <MenuItem key={module.id}
                      value={module.id}>
                      {module.name} ({module.estimatedDurationMinutes || 0} {t('trainingAssignments.min')})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              canAssignCoursesPacks && (
                <FormControl fullWidth
                  required>
                  <InputLabel>{bulkForm.contentType === 'course' ? t('trainingAssignments.course') : t('trainingAssignments.pack')}</InputLabel>
                  <Select
                    value={bulkForm.bundleId}
                    onChange={(e) => {
                      const selectedId = Number(e.target.value);
                      setBulkForm({ ...bulkForm, bundleId: selectedId, userIds: [] });
                    }}
                    label={bulkForm.contentType === 'course' ? t('trainingAssignments.course') : t('trainingAssignments.pack')}
                  >
                    <MenuItem value={0}>{t('trainingAssignments.selectBundle')}</MenuItem>
                    {bundles
                      .filter((bundle) => bundle.bundleType === (bulkForm.contentType === 'course' ? TrainingBundleType.Course : TrainingBundleType.Pack))
                      .map((bundle) => (
                        <MenuItem key={bundle.id}
                          value={bundle.id}>
                          {bundle.name} ({bundle.modules.length} {t('trainingAssignments.modulesLabel')})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )
            )}

            {/* Assignment Type */}
            <FormControl fullWidth
              required>
              <InputLabel>{t('trainingAssignments.assignTo')}</InputLabel>
              <Select
                value={bulkForm.assignmentType}
                onChange={(e) =>
                  setBulkForm({
                    ...bulkForm,
                    assignmentType: e.target.value as 'user' | 'role' | 'store',
                    userIds: [],
                    roleIds: [],
                    storeNumbers: [],
                  })
                }
                label={t('trainingAssignments.assignTo')}
              >
                <MenuItem value="role">{t('trainingAssignments.jobRoles')}</MenuItem>
                <MenuItem value="user">{t('trainingAssignments.individualUsers')}</MenuItem>
                <MenuItem value="store">{t('trainingAssignments.stores')}</MenuItem>
              </Select>
            </FormControl>

            {/* Role Selection */}
            {bulkForm.assignmentType === 'role' && (
              <Autocomplete
                multiple
                options={roles}
                getOptionLabel={(option) => option.name}
                value={roles.filter((r) => bulkForm.roleIds.includes(r.id))}
                onChange={(event, newValue) => {
                  setBulkForm({ ...bulkForm, roleIds: newValue.map((v) => v.id) });
                }}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('trainingAssignments.selectJobRoles')}
                    required />
                )}
              />
            )}

            {/* User Selection */}
            {bulkForm.assignmentType === 'user' && (
              <Autocomplete
                multiple
                options={bulkAssignUsers}
                getOptionLabel={(option) => ` ${option.email}`}
                value={bulkAssignUsers.filter((u) => bulkForm.userIds.includes(u.id))}
                onChange={(event, newValue) => {
                  setBulkForm({ ...bulkForm, userIds: newValue.map((v) => v.id) });
                }}
                loading={loadingBulkUsers}
                disabled={selectedContentId === 0}
                noOptionsText={selectedContentId === 0 ? (
                  bulkForm.contentType === 'module'
                    ? t('trainingAssignments.selectModuleFirst')
                    : t('trainingAssignments.selectBundle')
                ) : undefined}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('trainingAssignments.selectUsers')}
                    required />
                )}
              />
            )}

            {/* Store Selection */}
            {bulkForm.assignmentType === 'store' && (
              <Autocomplete
                multiple
                options={stores}
                getOptionLabel={(option) => `${option.storeName} (${getDisplayStoreNumber(option)})`}
                value={stores.filter((s) => bulkForm.storeNumbers.includes(s.storeNumber))}
                onChange={(event, newValue) => {
                  setBulkForm({ ...bulkForm, storeNumbers: newValue.map((v) => v.storeNumber) });
                }}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('trainingAssignments.selectStores')}
                    required />
                )}
              />
            )}

            {/* Due Date */}
            <DatePickerLocalized
              useCalendar={false}
              label={t('trainingAssignments.dueDate')}
              value={bulkForm.dueDate}
              onChange={(newValue) => setBulkForm({ ...bulkForm, dueDate: newValue || '' })}
              translations={{
                clearButtonLabel: t('calendar.clearButtonLabel'),
                todayButtonLabel: t('calendar.todayButtonLabel'),
                cancelButtonLabel: t('calendar.cancelButtonLabel'),
                okButtonLabel: t('calendar.okButtonLabel'),
              }}
            />

            {/* Notification Options */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkForm.sendNotification}
                  onChange={(e) => setBulkForm({ ...bulkForm, sendNotification: e.target.checked })}
                />
              }
              label={t('trainingAssignments.sendNotificationToAssignees')}
            />

            {bulkForm.sendNotification && (
              <TextField
                label={t('trainingAssignments.notificationMessageOptional')}
                multiline
                rows={3}
                fullWidth
                value={bulkForm.notificationMessage}
                onChange={(e) => setBulkForm({ ...bulkForm, notificationMessage: e.target.value })}
                placeholder={t('trainingAssignments.notificationPlaceholder')}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setBulkAssignDialogOpen(false)}>
            {t('trainingAssignments.cancel')}
          </Button>
          {canManageAssignments && (
            <Button variant="primary"
              onClick={handleBulkAssign}
              disabled={saving}>
              {t('trainingAssignments.assignTraining')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDeleteDialog
        open={deleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleDeleteCancel}
        nameOfObject={selectedAssignment
          ? `${selectedAssignment.userName} - ${selectedAssignment.bundleType === TrainingBundleType.Course
            ? selectedAssignment.bundleName
            : selectedAssignment.moduleName}`
          : ''}
      />
    </PageArea>
  );
};

export default TrainingAssignments;
